import { FC, useCallback, useMemo, useState } from 'react';
import { faClouds, faDownload, faWindow } from '@fortawesome/pro-regular-svg-icons';
import { faPlay } from '@fortawesome/pro-solid-svg-icons';
import { NotificationDispatch, useViewport } from '@tryhackme/thm-ui-components';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { setCurrentTab, setVmPanelOpen, setStaticSiteTabs, SplitScreenTab } from 'src/app/reducers/split-screen.reducer';
import { BREAKPOINTS } from 'src/common/constants/breakpoints';
import { TaskType } from 'src/common/enums/task';
import { DesktopOnlyAccessPopup } from 'src/features/room/components/desktop-only-access-popup';
import { useGetRoomDetailQuery, useLazyGetRoomTaskFilesDownloadUrlQuery, useTerminateVmMutation } from 'src/features/room/room.slice';
import { useStartVm } from 'src/common/hooks/use-start-vm';
import { useGetCompanyQuery, useGetUserQuery } from 'src/common/slices';
import { AzureCloudDetailsModal } from 'src/features/room/components/tasks/components/azure-cloud-details/azure-cloud-details-modal';
import { setVmTaskId, setVmUploadId } from 'src/app/reducers/room/room.reducer';
import { QueryErrorResponse } from 'src/common/interfaces';
import { useShouldSeeStartVmExpand } from '../../tasks.helpers';
import { CLOUD_DETAILS_MESSAGE, DEPLOYABLE_MACHINE_BUTTON_MESSAGE, DOWNLOADABLE_BUTTON_MESSAGE, STATIC_WEBSITE_BUTTON_MESSAGE } from './accordion.constants';
import { StyledAccordionDetailsTop, StyledContentButton, StyledContentButtonIcon } from './accordion.styles';
import { ContentButtonTypes } from './accordion.types';
export const ContentButton: FC<ContentButtonTypes> = ({
  type,
  staticSite,
  taskId,
  uploadId,
  roomId,
  roomCode,
  taskNo,
  applyDefaultStyles = true,
  isDesktopOnlyAccessPopupOpen,
  setIsDesktopOnlyAccessPopupOpen,
  hasSeenVmStart,
  cloud,
  isAttackboxWidgetV1ExperimentEnabled,
  isAttackboxWidgetV2ExperimentEnabled
}) => {
  const shouldSeeVmStart = useShouldSeeStartVmExpand(roomCode, taskNo);
  const dispatch = useAppDispatch();
  const [getRoomTaskFilesDownloadUrl] = useLazyGetRoomTaskFilesDownloadUrlQuery();
  const {
    startVmHandler,
    isVmStartLoading
  } = useStartVm(roomCode);
  const activeMachine = useAppSelector(state => state.room.activeMachine);
  const vmUploadId = useAppSelector(state => state.room.vmUploadId);
  const vmTaskId = useAppSelector(state => state.room.vmTaskId);
  const {
    data: userData,
    isSuccess: isUserAuth
  } = useGetUserQuery();
  const companyId = userData?.data?.user.subscriptions[0]?.companyId;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const {
    isLoading: isLoadingCompany
  } = useGetCompanyQuery(companyId!, {
    skip: !companyId || !cloud?.azure?.isMultiTenant
  });
  const {
    data: roomData
  } = useGetRoomDetailQuery(roomCode);
  const hasUserJoinedRoom = roomData?.data?.joined;
  const [showCloudDetailsModal, setShowCloudDetailsModal] = useState(false);
  // viewport
  const {
    width
  } = useViewport();
  const hasAnotherRunningVm = useMemo(() => activeMachine?.id && activeMachine.id !== uploadId, [activeMachine?.id, uploadId]);
  const [terminateVM] = useTerminateVmMutation();
  const onPressDownloadTaskFileUrlButton = async () => {
    try {
      const presignedUrl = await getRoomTaskFilesDownloadUrl({
        roomId: roomId ?? '',
        downloadId: uploadId,
        taskId: taskId ?? ''
      });
      const filename = presignedUrl.data?.data?.url?.split('?')[0]?.split('vm-upload/')[1] ?? '';
      const link = document.createElement('a');
      link.href = presignedUrl.data?.data?.url ?? '';
      link.download = filename;
      document.body.append(link);
      link.click();
      link.remove();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to download task file:', error);
    }
  };
  const handleOnClickStaticSite = useCallback(() => {
    if (width < BREAKPOINTS.VM) {
      setIsDesktopOnlyAccessPopupOpen(true);
    } else if (staticSite) {
      const tab: SplitScreenTab = {
        id: staticSite.title,
        type: TaskType.STATIC_SITE,
        title: staticSite.title,
        url: staticSite.url,
        staticSite
      };
      dispatch(setStaticSiteTabs({
        tabs: [tab]
      }));
      dispatch(setCurrentTab(tab));
      dispatch(setVmPanelOpen(true));
    } else {
      NotificationDispatch('error', 'Static site info not found');
    }
  }, [dispatch, staticSite, width, setIsDesktopOnlyAccessPopupOpen]);
  const executeVmClickAction = async () => {
    if (width < BREAKPOINTS.VM) {
      setIsDesktopOnlyAccessPopupOpen(true);
    } else {
      await startVmHandler('Deploy', uploadId, taskId);
    }
  };
  const handleTerminateMachine = async () => {
    try {
      if (!activeMachine) return;
      await terminateVM(activeMachine.id);
      NotificationDispatch('success', `Hooray! Successfully terminated instance`);
    } catch (error: unknown) {
      const errorRes = (error as QueryErrorResponse);
      const message = errorRes?.data?.message || 'Something went wrong. Please try again.';
      NotificationDispatch('error', `Uh-oh! ${message}`);
    }
  };
  const handleDeployMachine = () => {
    dispatch(setVmUploadId(uploadId));
    dispatch(setVmTaskId(taskId ?? ''));
    // eslint-disable-next-line no-void
    void executeVmClickAction();
  };
  const handleTerminateExistingAndDeployMachine = async () => {
    await handleTerminateMachine();
    handleDeployMachine();
  };
  const handleCloudDetailsClick = () => {
    setShowCloudDetailsModal(true);
  };
  if (type === TaskType.DOWNLOADABLE) return <StyledAccordionDetailsTop applyDefaultStyles={applyDefaultStyles}>
        <StyledContentButton color="add"
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    onClick={onPressDownloadTaskFileUrlButton} applyDefaultStyles={applyDefaultStyles} isDisabled={!isUserAuth || !hasUserJoinedRoom}>
          <StyledContentButtonIcon icon={faDownload} />
          {DOWNLOADABLE_BUTTON_MESSAGE}
        </StyledContentButton>
      </StyledAccordionDetailsTop>;
  if (type === TaskType.VM || type === TaskType.THM) return <>
        <StyledAccordionDetailsTop applyDefaultStyles={applyDefaultStyles} $isExperimentEnabled={isAttackboxWidgetV1ExperimentEnabled || isAttackboxWidgetV2ExperimentEnabled}>
          <StyledContentButton id={`start-machine-button-${taskNo}`} isDisabled={!isUserAuth || !hasUserJoinedRoom || (isAttackboxWidgetV2ExperimentEnabled ? activeMachine?.uploadId === uploadId : shouldSeeVmStart && !hasSeenVmStart || !!activeMachine)}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onClick={isAttackboxWidgetV2ExperimentEnabled && hasAnotherRunningVm ? handleTerminateExistingAndDeployMachine : handleDeployMachine} isLoading={isVmStartLoading} applyDefaultStyles={applyDefaultStyles}>
            <StyledContentButtonIcon icon={faPlay} />
            {DEPLOYABLE_MACHINE_BUTTON_MESSAGE}
          </StyledContentButton>
        </StyledAccordionDetailsTop>
        <DesktopOnlyAccessPopup open={isDesktopOnlyAccessPopupOpen} onOpenChange={setIsDesktopOnlyAccessPopupOpen} closeAction={() => {
      // eslint-disable-next-line no-void
      void startVmHandler('Deploy', vmUploadId, vmTaskId);
    }} splitScreenType={TaskType.VM} />
      </>;
  if (type === TaskType.STATIC_SITE) return <>
        <StyledAccordionDetailsTop applyDefaultStyles={applyDefaultStyles}>
          <StyledContentButton onClick={handleOnClickStaticSite} applyDefaultStyles={applyDefaultStyles} isDisabled={!isUserAuth || !hasUserJoinedRoom}>
            <StyledContentButtonIcon icon={faWindow} />
            {STATIC_WEBSITE_BUTTON_MESSAGE}
          </StyledContentButton>
        </StyledAccordionDetailsTop>
        <DesktopOnlyAccessPopup open={isDesktopOnlyAccessPopupOpen} onOpenChange={setIsDesktopOnlyAccessPopupOpen} closeAction={() => {
      if (staticSite) {
        const tab: SplitScreenTab = {
          id: staticSite.title,
          type: TaskType.STATIC_SITE,
          title: staticSite.title,
          url: staticSite.url,
          staticSite
        };
        dispatch(setStaticSiteTabs({
          tabs: [tab]
        }));
        dispatch(setCurrentTab(tab));
        dispatch(setVmPanelOpen(true));
      }
    }} splitScreenType={TaskType.STATIC_SITE} />
      </>;
  if (type === TaskType.AZURE && taskId && uploadId && cloud?.azure?.isAzureTrainingEnabled) {
    const latestTaskLab = [...(cloud?.azure?.azureLabs ?? [])]?.sort((a, b) => a.expires < b.expires ? 1 : -1)?.find(lab => lab.taskId === taskId);
    return <>
        <StyledAccordionDetailsTop applyDefaultStyles={applyDefaultStyles}>
          <StyledContentButton onClick={handleCloudDetailsClick} applyDefaultStyles={applyDefaultStyles} isDisabled={!isUserAuth || !hasUserJoinedRoom}>
            <StyledContentButtonIcon icon={faClouds} />
            {CLOUD_DETAILS_MESSAGE}
          </StyledContentButton>
        </StyledAccordionDetailsTop>
        {roomId && <AzureCloudDetailsModal lab={latestTaskLab} roomId={roomId} companyRefreshing={isLoadingCompany} taskId={taskId} open={showCloudDetailsModal} onOpenChange={setShowCloudDetailsModal} />}
      </>;
  }
  return null;
};